import type { BookingRecord, DateRange, ServiceType, TripStatus } from '../types'

export const serviceTypes: ServiceType[] = [
  'Transfers',
  'Hourly Drivers',
  'Day Trips',
]

export const tripStatuses: TripStatus[] = [
  'upcoming',
  'on-trip',
  'completed',
  'cancelled',
  'confirmed',
  'pending',
  'incomplete',
]

export const tripStatusLabels: Record<TripStatus, string> = {
  upcoming: 'Upcoming',
  'on-trip': 'On trip',
  completed: 'Completed',
  cancelled: 'Cancelled',
  confirmed: 'Confirmed',
  pending: 'Pending',
  incomplete: 'Incomplete',
}

export const dateRanges: DateRange[] = [
  { id: 'jun-2026', label: '01 Jun 2026 — 30 Jun 2026', from: '2026-06-01', to: '2026-06-30' },
  { id: 'jul-2026', label: '01 Jul 2026 — 31 Jul 2026', from: '2026-07-01', to: '2026-07-31' },
  { id: 'aug-2026', label: '01 Aug 2026 — 31 Aug 2026', from: '2026-08-01', to: '2026-08-31' },
  { id: 'q3-2026', label: '01 Jul 2026 — 30 Sep 2026', from: '2026-07-01', to: '2026-09-30' },
  { id: 'h2-2026', label: '01 Jul 2026 — 31 Dec 2026', from: '2026-07-01', to: '2026-12-31' },
]

/**
 * Stand-in rows mirroring the placeholder content in the design. Swap this module for an
 * API call when the backend lands; nothing else in the page reads booking data directly.
 */
const template = {
  customer: { name: 'John Smith', email: 'john@email.com' },
  reference: 'SCB-10254',
  service: 'Transfers' as const,
  route: { from: 'Hallstatt', to: 'vienna' },
  scheduledAt: '2026-06-17T15:00:00',
  vehicle: 'VW Passat',
  amountEur: 85,
}

type RowState = Pick<BookingRecord, 'driverAssigned' | 'payment' | 'trip'>

const rowStates: RowState[] = [
  { driverAssigned: true, payment: 'paid', trip: 'completed' }, // will make today in map
  { driverAssigned: true, payment: 'paid', trip: 'on-trip' },
  { driverAssigned: false, payment: 'paid', trip: 'upcoming' },
  { driverAssigned: true, payment: 'paid', trip: 'on-trip' },
  { driverAssigned: true, payment: 'paid', trip: 'on-trip' },
  { driverAssigned: true, payment: 'paid', trip: 'on-trip' }, // will make high value in map
  { driverAssigned: false, payment: 'unpaid', trip: 'upcoming' },
  { driverAssigned: false, payment: 'paid', trip: 'cancelled' },
  { driverAssigned: false, payment: 'unpaid', trip: 'upcoming' },
  { driverAssigned: false, payment: 'paid', trip: 'completed' },
]

export const bookings: BookingRecord[] = rowStates.map((state, index) => {
  let service: ServiceType = 'Transfers';
  if (index % 3 === 1) service = 'Hourly Drivers';
  if (index % 3 === 2) service = 'Day Trips';

  let amountEur = template.amountEur;
  if (index === 5) amountEur = 750; // High Value

  let scheduledAt = template.scheduledAt;
  if (index === 0) {
    // Today
    const today = new Date();
    scheduledAt = today.toISOString().slice(0, 19);
  }

  const assignedDriver = state.driverAssigned
    ? {
        driverId: index % 2 === 0 ? 'driver-1' : 'driver-2',
        driverName: index % 2 === 0 ? 'Amal Ahammed' : 'Marco Rossi',
        driverAmount: Math.round(amountEur * 0.75),
      }
    : {
        driverId: undefined,
        driverName: undefined,
        driverAmount: 0,
      };

  return {
    id: `booking-${index + 1}`,
    ...template,
    amountEur,
    scheduledAt,
    service,
    ...state,
    ...assignedDriver,
  };
})
